// ADR 19.9.26c §1 — the handoff from `index.html`'s `#static-shell`.
//
// It renders nothing; its one job is to be a *commit*. `useLayoutEffect` runs
// after React has committed and before the browser paints that commit, so the
// static document a crawler read stays on screen through the whole
// asynchronous bootstrap (theme → i18n → render) and then disappears in
// the same frame the app appears in: no blank interval, no double-painted
// page. Every state that never reaches a commit therefore keeps the shell —
// a rejected `initI18n`, a missing `#root`, and `reportFatal` revealing the
// `fatal` template *beside* it (6.9.26c §2). ADR 25.9.26f §4 — `refuseRender`
// is no longer one of them: the wasm is awaited *after* the render now, so its
// refusal unmounts a root that has already committed and removed the shell.
//
// `main.tsx` renders it as the first child of `StrictMode` and a **sibling**
// of `RootErrorBoundary`, not a child: a committed error UI is still a commit,
// React owns the page, and the shell must go.
//
// Its own module rather than a function inside `main.tsx` for one reason:
// `main.tsx` calls `bootstrap()` at the top level, so a test that imported it
// would start the real application. The claim AC2 makes — "removed only on a
// commit" — is a claim about this component, and this is where a test can
// reach it.
//
// ADR 23.9.26c §1 — and it reports what it found, as `web_app_opened`'s
// `static_shell`. `removed` / `absent` are recorded synchronously, because the
// shell's launch effect is a passive effect of this same commit and must see
// them; the `rAF` then upgrades to `stuck` if the shell survived to the next
// frame. `deliverLaunch` reads the value at transmit time, so a retried launch
// carries the upgrade — a first-attempt one may not, which is why `stuck` is a
// tripwire rather than a rate. A pre-commit failure never gets here, so it
// reports nothing rather than `stuck`: that shell is *meant* to stay.
//
// ADR 1.10.26p §2 — and a second job, because it already *is* the first commit
// by definition: it marks the first-paint signal (`firstPaint.ts`) that the
// unhinted wasm warm-up and Google's script wait for. First statement, ahead
// of the `absent` early return, so a document without the shell still marks.

import { useLayoutEffect } from 'react'

import { setStaticShell } from '../layout/analytics'
import { markFirstCommit } from './firstPaint'

/** The element `index.html` ships beside `#root`, removed on React's first commit. */
export const STATIC_SHELL_ID = 'static-shell'

export function StaticShellCommit(): null {
  useLayoutEffect(() => {
    markFirstCommit()
    const shell = document.getElementById(STATIC_SHELL_ID)
    if (shell === null) {
      setStaticShell('absent')
      return
    }
    shell.remove()
    setStaticShell('removed')
    requestAnimationFrame(() => {
      if (document.getElementById(STATIC_SHELL_ID) !== null) setStaticShell('stuck')
    })
  }, [])
  return null
}
